/**
 * AppDataGrid – a thin wrapper around DataGridPro that automatically applies
 * a smart sort comparator to every column.
 *
 * Numeric strings (e.g. "1", "10", "100") are sorted numerically (1 → 2 → 3…)
 * rather than lexicographically (1 → 10 → 100…).  All other values fall back
 * to a locale-aware string comparison.
 *
 * Usage: drop-in replacement for DataGridPro – just change the import:
 *   import AppDataGrid from "../../common/AppDataGrid";
 *   <AppDataGrid ... />
 */
import React from "react";
import { DataGridPro } from "@mui/x-data-grid-pro";

/**
 * Compares two values smartly:
 * - If both parse to finite numbers, sort numerically.
 * - Otherwise sort as locale strings.
 */
export const smartSortComparator = (v1, v2) => {
  const n1 = Number(v1);
  const n2 = Number(v2);
  if (!isNaN(n1) && isFinite(n1) && !isNaN(n2) && isFinite(n2)) {
    return n1 - n2;
  }
  return String(v1 ?? "").localeCompare(String(v2 ?? ""), undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

/**
 * Enhances a columns array so every column that doesn't already have a
 * custom sortComparator gets the smart one.
 */
const enhanceColumns = (columns) => {
  if (!Array.isArray(columns)) return columns;
  return columns.map((col) =>
    col.sortComparator ? col : { ...col, sortComparator: smartSortComparator },
  );
};

const AppDataGrid = ({ columns, ...props }) => {
  const enhancedColumns = React.useMemo(
    () => enhanceColumns(columns),
    [columns],
  );

  return <DataGridPro columns={enhancedColumns} {...props} />;
};

export default AppDataGrid;
